import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { memo, useMemo } from "react";
import { Card, Checkbox, Typography, Badge } from "antd";
import { useTripCardHooks } from "./TripCard.hooks";
import { colors } from "@root/utils/colors";
import { withLineBreakRaw } from "@root/utils/rlib";
import sift from "sift";
import * as Ant from "antd";
import { FilePdfOutlined, FileTextOutlined } from "@ant-design/icons";
import { mutate } from "@root/store";
import axios from "axios";
const toDate = (appointment) => ({
    ...appointment,
    endAt: new Date(appointment.endAt),
    startAt: new Date(appointment.startAt),
});
const getFirstAppointmentDate = (appointments) => {
    let min = Infinity;
    appointments.forEach((appointment) => {
        min = Math.min(min, appointment.startAt.getTime());
    });
    return new Date(min);
};
const getLastAppointmentDate = (appointments) => {
    let max = 0;
    appointments.forEach((appointment) => {
        max = Math.max(max, appointment.endAt.getTime());
    });
    return new Date(max);
};
const cancelOnSiteJob = (missionId, onSiteJobId, reason) => {
    const token = window.localStorage.getItem("bluelibs-token");
    axios.request({
        method: "POST",
        url: global.NEW_API_URL + '/onSiteJobs/' + onSiteJobId + '/cancel',
        headers: { "Authorization": token },
        data: {
            cancellationReason: reason,
        },
    }).then(() => {
        window.location.reload();
    });
};
const getTypeIcon = (mimeType) => {
    if (mimeType === "application/pdf") {
        return _jsx(FilePdfOutlined, {});
    }
    return _jsx(FileTextOutlined, {});
};
const FileGroup = memo((props) => {
    const imageTypeFilter = sift({
        type: { $regex: "^image/", $options: "i" },
    });
    const nonImageTypeFilter = sift({
        type: { $not: { $regex: "^image/", $options: "i" } },
    });
    const images = props.attachments.filter(imageTypeFilter);
    const nonImages = props.attachments.filter(nonImageTypeFilter);
    const imgPreviewGrpEllipsisConfig = {
        rows: 10,
        expandable: true,
        symbol: "afficher plus d'images",
    };
    return (_jsxs("div", { className: props.className, children: [_jsx(Ant.Image.PreviewGroup, { children: _jsx(Ant.Typography.Paragraph, { ellipsis: imgPreviewGrpEllipsisConfig, className: "flex wrap gap-1/2", children: images.map((img) => (_jsx(Ant.Image, { height: 80, width: 80, src: `${window.location.host === 'belett2.selfcity.fr' ? 'https://api.belett.selfcity.fr' : window.location.host === 'staging.belett.selfcity.fr' ? 'https://staging.api.belett.selfcity.fr' : 'http://localhost:4000'}/documents/${img.path.replace('attachments/', '')}`, alt: img.name, preview: { src: `${window.location.host === 'belett2.selfcity.fr' ? 'https://api.belett.selfcity.fr' : window.location.host === 'staging.belett.selfcity.fr' ? 'https://staging.api.belett.selfcity.fr' : 'http://localhost:4000'}/documents/${img.path.replace('attachments/', '')}` } }, img.path))) }) }), _jsx("div", { className: "mar-t-1", children: nonImages.map((f) => (_jsx(Ant.Tag, { icon: getTypeIcon(f.type), color: colors.selfcityGreen.opacity(0.15), className: "fc-primary mar-0 mar-b-1/2 mar-r-1", children: _jsx("a", { href: `${window.location.host === 'belett2.selfcity.fr' ? 'https://api.belett.selfcity.fr' : window.location.host === 'staging.belett.selfcity.fr' ? 'https://staging.api.belett.selfcity.fr' : 'http://localhost:4000'}/documents/${f.path.replace('attachments/', '')}`, target: "_blank", className: "fc-inherit", children: f.name }) }, f._id))) })] }));
});
const types = {
    PAID_WORK: 'Intervention',
    AFTER_SALE_SERVICE: 'SAV',
    DIAGNOSTIC: 'Devis / Diagnostic',
};
const breakSpaces = { whiteSpace: "break-spaces" };
const removeMarginBottom = { marginBottom: 0 };
const removePaddingBottom = { paddingBottom: 0 };
const seeMoreConfig = {
    rows: 100,
    expandable: true,
    symbol: "Voir plus",
};
const buildNotes = (report) => {
    var _a, _b, _c, _d;
    return []
        .concat(report.wasCancelled
        ? [{ text: `Le RDV est annulé. ${(_a = report.cancellationReason) !== null && _a !== void 0 ? _a : ''}` }]
        : [])
        .concat(report.createNewMission
        ? [{ text: 'L\'intervention est terminée, mais un nouveau devis est à faire (une nouvelle mission a dû être créée automatiquement).' }]
        : [])
        .concat(report.needsNewOnSiteJob && report.actualType === 'DIAGNOSTIC'
        ? [{ text: 'Il faut maintenant faire le devis suite au RDV.' }]
        : [])
        .concat(report.needsNewOnSiteJob && report.actualType === 'PAID_WORK'
        ? [{ text: 'L\'intervention n\'est pas terminée, il faut revenir pour finir l\'intervention.' }]
        : [])
        .concat(!report.isReadyForBilling && report.actualType === 'PAID_WORK'
        && !report.needsNewOnSiteJob && !report.createNewMission
        ? [{ text: 'L\'intervention n\'est pas terminée.' }]
        : [])
        .concat(((_b = report.details.intervention) === null || _b === void 0 ? void 0 : _b.isSolved) && report.actualType === 'AFTER_SALE_SERVICE'
        ? [{ text: 'Mission terminée, problème résolu.' }]
        : [])
        .concat(!((_c = report.details.intervention) === null || _c === void 0 ? void 0 : _c.isSolved) && report.actualType === 'AFTER_SALE_SERVICE'
        ? [{ text: 'Suite au RDV SAV, il faut encore revenir chez le client.' }]
        : [])
        .concat(!((_d = report.details.intervention) === null || _d === void 0 ? void 0 : _d.isSolved) && report.actualType === 'AFTER_SALE_SERVICE' && report.createNewMission
        ? [{ text: 'Suite au RDV SAV, il faut faire un devis.' }]
        : [])
        .concat((report.isReadyForBilling && report.paymentPicture !== null)
        ? [{ text: 'L\'intervention est terminée, le client a payé par chèque.' }]
        : [])
        .concat((report.isReadyForBilling && report.paymentPicture === null)
        ? [{ text: 'L\'intervention est terminée, le client va payer plus tard (chèque, virement ou via CB en ligne).' }]
        : [])
        .concat([{ text: 'CR (visible par le client) : ' + report.publicNotes }])
        .concat([{ text: 'CR (non-visible par le client) : ' + report.privateNotes }])
        .concat(report.isResidentialBuilding
        ? [{ text: 'Ce lieu est à usage d\'habitation et a été construit il y a plus de 2 ans. Le client peut légalement bénéficier d\'une TVA à 10%.' }]
        : [{ text: 'Ce lieu n\'est pas à usage d’habitation ou n\'a pas été construit il y a plus de 2 ans. Le client ne peut pas légalement bénéficier d\'une TVA à 10%.' }])
        .concat([
        { text: 'Date : ' + new Date(report.startedAt).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' }) + '\n' + 'Heure darrivée : ' + new Date(report.startedAt).toLocaleString('en-EN', { timeZone: 'Europe/Paris' }).split(' ')[1].split(':').slice(0, 2).join('h') + '\n' + 'Durée de lintervention : ' + ((new Date(report.endedAt).getTime() - new Date(report.startedAt).getTime()) / (3600 * 1000)).toFixed(2) + 'h' },
    ]).reduce((acc, line) => acc + '\n----------\n' + line.text, '');
};
const getMidnightTimestamp = (date) => new Date(date).setHours(0, 0, 0);
const getMinifiedName = (profile) => `${profile.firstName} ${profile.lastName.charAt(0)}.`;
const getEventsPerDayPerWorker = (appointments) => {
    if (appointments.length <= 0) {
        return {};
    }
    const result = appointments.reduce((accEventsPerDay, currCalEvnt) => {
        const timeStamp = getMidnightTimestamp(new Date(currCalEvnt.startAt)).toString();
        const workerId = currCalEvnt.user._id;
        const workerMinifiedName = getMinifiedName(currCalEvnt.user);
        if (timeStamp in accEventsPerDay) {
            if (workerId in accEventsPerDay[timeStamp]) {
                accEventsPerDay[timeStamp][workerId] = {
                    minifiedName: workerMinifiedName,
                    tripsHours: [
                        ...accEventsPerDay[timeStamp][workerId].tripsHours,
                        {
                            startAt: new Date(currCalEvnt.startAt).getTime(),
                            // endAt: currCalEvnt.plannedDepartureAt,
                            duration: (new Date(currCalEvnt.endAt).getTime() - new Date(currCalEvnt.startAt).getTime()),
                        },
                    ].sort((tripHourA, tripHourB) => tripHourA.startAt - tripHourB.startAt),
                    // tripsTotalTime:
                    //   accEventsPerDay[timeStamp][workerId].tripsTotalTime + currCalEvnt.plannedDuration,
                };
            }
            else {
                accEventsPerDay[timeStamp] = {
                    ...accEventsPerDay[timeStamp],
                    [workerId]: {
                        minifiedName: workerMinifiedName,
                        tripsHours: [
                            {
                                startAt: new Date(currCalEvnt.startAt),
                                // endAt: currCalEvnt.plannedDepartureAt,
                                duration: (new Date(currCalEvnt.endAt).getTime() - new Date(currCalEvnt.startAt).getTime()),
                            },
                        ],
                        // tripsTotalTime: currCalEvnt.plannedDuration,
                    },
                };
            }
        }
        else {
            accEventsPerDay[timeStamp] = {
                [workerId]: {
                    minifiedName: workerMinifiedName,
                    tripsHours: [
                        {
                            startAt: new Date(currCalEvnt.startAt),
                            // endAt: currCalEvnt.plannedDepartureAt,
                            duration: (new Date(currCalEvnt.endAt).getTime() - new Date(currCalEvnt.startAt).getTime()),
                        },
                    ],
                    // tripsTotalTime: currCalEvnt.plannedDuration,
                },
            };
        }
        return accEventsPerDay;
    }, {});
    const orderedEventsPerDayPerWorker = Object.keys(result)
        .sort((a, b) => Number(a) - Number(b))
        .reduce((obj, key) => {
        obj[key] = result[key];
        return obj;
    }, {});
    return orderedEventsPerDayPerWorker;
};
export const TripCard = memo((props) => {
    var _a, _b, _c, _d;
    const { t, tripListDatesTimeString, workersNames, tripReport, isTripReportActionToTake, onProcessedCheck, tripListState, isPlumber, getEventsPerDayPerWorkerTripHoursString, getDayString, } = useTripCardHooks(props);
    const cardBackgroundColor = useMemo(() => {
        return {
            backgroundColor: tripListState.isUpcoming
                ? colors.tripBlue.opacity(0.15)
                : tripListState.isInProgress
                    ? colors.selfcityYellow.opacity(0.15)
                    : isTripReportActionToTake
                        ? colors.selfcityRed.opacity(0.15)
                        : "white",
        };
    }, [isTripReportActionToTake]);
    const openOnSiteJobModal = React.useCallback((clientRequestId, onSiteJobId, isASS) => {
        return () => {
            const token = window.localStorage.getItem("bluelibs-token");
            window.location.href = isASS
                ? global.NEW_APP_URL + '/missions/' + clientRequestId + '/onSiteJobs/create?token=' + token + '&onSiteJobId=' + onSiteJobId
                : global.NEW_APP_URL + '/missions/' + clientRequestId + '/onSiteJobs/' + onSiteJobId + '/edit?token=' + token;
        };
    }, []);
    const syncAgenda = React.useCallback((clientRequestId, onSiteJobId) => () => {
        const token = window.localStorage.getItem("bluelibs-token");
        axios({
            method: 'PUT',
            url: `${global.NEW_API_URL}/onSiteJobs/${onSiteJobId}`,
            headers: {
                Authorization: token,
            },
            data: {},
        }).then(() => {
            alert('Synchronisation effectuée.');
        });
    }, []);
    const openCancellationModal = React.useCallback(() => {
        //
        mutate("modal", "SHOW", {
            component: "onSiteJobCancellation",
            componentProps: {
                startDate: new Date(props.tripList.data.appointments[0].startAt),
                onConfirm: (reason) => cancelOnSiteJob(props.tripList.data.appointments[0]._mission, props.tripList.data._id, reason),
            },
        });
    }, [cancelOnSiteJob, props.tripList.data.appointments[0]._mission, props.tripList.data._id]);
    return (_jsxs(_Fragment, { children: [_jsxs(Card, { size: "small", className: "child-pad-1-i", bodyStyle: cardBackgroundColor, children: [_jsxs("table", { className: 'trip-card-table', children: [props.tripList.data._cancellationReason !== null && _jsx("span", { style: { color: 'rgba(255, 0, 0, 0.5)' }, children: "INTERVENTION ANNUL\u00C9E" }), props.tripList.data._cancellationReason === null && (getLastAppointmentDate(props.tripList.data.appointments.map(toDate)).getTime() >= new Date((new Date()).toISOString().split('T')[0]).getTime() && !props.tripList.data.report) && _jsx(Ant.Button, { style: { margin: '0.5rem 1rem' }, onClick: syncAgenda(props.tripList.data.appointments[0]._mission, props.tripList.data._id), children: "Synchro agenda" }), _jsxs("div", { style: { display: 'flex' }, children: [props.tripList.data._cancellationReason === null && (getLastAppointmentDate(props.tripList.data.appointments.map(toDate)).getTime() >= new Date((new Date()).toISOString().split('T')[0]).getTime() && !props.tripList.data.report) && _jsx(Ant.Button, { style: { margin: '0 1rem' }, onClick: openOnSiteJobModal(props.tripList.data.appointments[0]._mission, props.tripList.data._id, false), children: "Modifier" }), props.tripList.data._cancellationReason === null && (getFirstAppointmentDate(props.tripList.data.appointments.map(toDate)).getTime() >= new Date((new Date()).toISOString().split('T')[0]).getTime() && !props.tripList.data.report) && _jsx(Ant.Button, { style: { margin: '0 1rem' }, onClick: openCancellationModal, children: "Annuler" }), props.tripList.data._cancellationReason === null && (getLastAppointmentDate(props.tripList.data.appointments.map(toDate)).getTime() < new Date((new Date()).toISOString().split('T')[0]).getTime()) && _jsx(Ant.Button, { style: { margin: '0', background: 'rgb(245, 34, 45)', color: 'white' }, onClick: openOnSiteJobModal(props.tripList.data.appointments[0]._mission, props.tripList.data._id, true), children: "Planifier un SAV" })] }), props.tripList.data.report !== null && (_jsxs("tr", { children: [_jsx("th", { children: "Compte Rendu" }), _jsx("td", { children: _jsx(Typography.Paragraph, { style: removeMarginBottom, ellipsis: seeMoreConfig, className: "display-linebreak", children: withLineBreakRaw(buildNotes(props.tripList.data.report))
                                                .replace(/<br \/>/g, '\n')
                                                .replace(/<[^>]*>/g, '') }) })] })), _jsxs("tr", { children: [_jsx("th", { children: "Type" }), _jsx("td", { children: types[props.tripList.data.type] })] }), _jsx("tr", { children: _jsx("th", { children: tripListState.isInProgress ? (_jsx(Badge, { status: "processing", text: "D\u00E9placement(s)" })) : ("Déplacement(s)") }) }), Object.entries(getEventsPerDayPerWorker(props.tripList.data.appointments)).map(([eventsDay, eventsPerWorker]) => (_jsxs("tr", { children: [_jsx("td", { children: _jsx("ul", { children: _jsx("li", { children: getDayString(eventsDay) }) }) }), Object.values(eventsPerWorker)
                                        .map((event) => (_jsx("tr", { children: getEventsPerDayPerWorkerTripHoursString(event) })))] }))), _jsxs("tr", { children: [_jsx("th", { children: "Technicien(s)" }), _jsx("td", { children: workersNames.join(", ") })] })] }), _jsx("div", { className: "ta-center fw-bold", children: props.tripList.data.report !== null ? (props.tripList.data.report._isProcessed ? (_jsx("span", { className: "fc-sc-green", children: "Compte-rendu trait\u00E9" })) : (_jsx(Checkbox, { checked: props.tripList.data.report._isProcessed, onChange: (e) => {
                                onProcessedCheck(e.target.checked);
                            }, disabled: isPlumber, children: _jsx("span", { className: "fc-sc-red", children: "Compte-rendu trait\u00E9 ?" }) }))) : (getLastAppointmentDate(props.tripList.data.appointments.map(toDate)).getTime() < Date.now() && !props.tripList.data.isCancelled && (_jsx("span", { className: "fc-sc-red", children: "Le compte-rendu n\u2019a toujours pas \u00E9t\u00E9 envoy\u00E9 par le technicien" }))) })] }), ((_a = props.tripList.data.report) === null || _a === void 0 ? void 0 : _a.paymentPicture) && (_jsx(Ant.Image, { height: 80, width: 80, src: props.tripList.data.report.paymentPicture, preview: props.tripList.data.report.paymentPicture })), ((_c = (_b = props.tripList.data.report) === null || _b === void 0 ? void 0 : _b.attachments.length) !== null && _c !== void 0 ? _c : 0) > 0 && (_jsx(FileGroup, { className: "mar-t-1 timeline-comment-img", attachments: (_d = props.tripList.data.report) === null || _d === void 0 ? void 0 : _d.attachments }))] }));
});
